import React, { useState, useMemo, useRef, useEffect } from "react";
import { Flag, List, Calendar, Star, Info } from "lucide-react";
import type { DataRecord, SocialOverviewPayload } from "../../../types";
import { ChartTooltip, type ChartTooltipItem, type ChartHitoItem } from "../../common/ChartTooltip";
import { RegisterHitoModal } from "../../RegisterHitoModal";
import { ManageHitosModal } from "../../ManageHitosModal";
import { useDashboard } from "../../../context/DashboardContext";
import { fetchClientHitos, deleteClientHito } from "../../../api";

interface Props {
  clientSlug: string;
  activePlatform: string;
  comments: DataRecord[];
  allComments?: DataRecord[];
  overview: SocialOverviewPayload | null;
  windowDays: number;
  starCounts: { counts: Record<number, number>; total: number };
}

export function SocialTrendSection({
  clientSlug,
  activePlatform,
  comments,
  allComments,
  overview,
  windowDays,
  starCounts,
}: Props) {
  const [hoveredSocialIdx, setHoveredSocialIdx] = useState<number | null>(null);
  const [pinnedIdx, setPinnedIdx] = useState<number | null>(null);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const [isHitoModalOpen, setIsHitoModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [editingHito, setEditingHito] = useState<ChartHitoItem | null>(null);
  const [localHitos, setLocalHitos] = useState<any[] | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { session } = useDashboard();
  const canManageHitos = Boolean(session?.canManageHitos);

  const loadHitos = async () => {
    try {
      const data = await fetchClientHitos(clientSlug);
      setLocalHitos(data);
    } catch (err) {
      console.warn("No fue posible cargar hitos para", clientSlug, err);
    }
  };

  useEffect(() => {
    loadHitos();
  }, [clientSlug]);

  const handleDeleteHito = async (h: ChartHitoItem) => {
    if (!h.id) return;
    if (!window.confirm(`¿Seguro que deseas eliminar el hito "${h.title}"?`)) return;
    try {
      await deleteClientHito({ id_item: h.id, client_slug: clientSlug });
      await loadHitos();
    } catch (err: any) {
      alert(err?.message || "No fue posible eliminar el hito");
    }
  };

  const text = (v: any, fallback = "") => (v !== null && v !== undefined ? String(v) : fallback);

  const isMetaIg = activePlatform === "meta_ig" || activePlatform === "instagram";
  const isMetaFb = activePlatform === "meta_fb" || activePlatform === "facebook";
  const isGoogle = activePlatform === "gbp" || activePlatform === "google";
  const isGlobal = activePlatform === "all" || !activePlatform;

  const hasGoogleData = useMemo(() => {
    const pool = allComments && allComments.length > 0 ? allComments : comments;
    return pool.some(
      (c) => text(c.platform).toLowerCase() === "gbp" && c.star_rating !== null && c.star_rating !== undefined
    );
  }, [allComments, comments]);

  const showGoogleRatings = isGoogle || (isGlobal && hasGoogleData);

  const platformBadgeLabel = isMetaIg
    ? "Instagram"
    : isMetaFb
    ? "Facebook"
    : isGoogle
    ? "Google BP"
    : "Visión Global";

  // Cálculo de notas mensuales de Google BP para los últimos 12 meses (datos reales)
  const monthlyGooglePoints = useMemo(() => {
    const anchor = new Date();
    const months: { key: string; label: string; dateStr: string; x: number }[] = [];
    const monthNames = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(anchor.getFullYear(), anchor.getMonth() - i, 1);
      const yr = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, "0");
      const key = `${yr}-${mo}`;
      const label = `${monthNames[d.getMonth()]} ${String(yr).slice(2)}`;
      const x = 50 + (11 - i) * (1100 / 11);
      months.push({ key, label, dateStr: `${key}-01`, x });
    }

    const pool = allComments && allComments.length > 0 ? allComments : comments;
    const gbpComments = pool.filter((c) => {
      const plat = String(c.platform || "").toLowerCase();
      return (plat === "gbp" || plat.includes("google")) && c.star_rating !== null && c.star_rating !== undefined && !c.is_owner_author;
    });

    return months.map((m) => {
      const inMonth = gbpComments.filter((c) => String(c.date || "").startsWith(m.key));
      const count = inMonth.length;
      let val: number | null = null;
      let y: number | null = null;
      if (count > 0) {
        const sum = inMonth.reduce((acc, c) => acc + Number(c.star_rating || 0), 0);
        val = Math.round((sum / count) * 10) / 10;
        // Escala SVG: 5.0 -> y=35, 1.0 -> y=165
        y = Math.round(165 - ((val - 1) / 4) * 130);
      }
      return {
        ...m,
        count,
        val,
        y,
      };
    });
  }, [allComments, comments]);

  // Hitos de ejecución vinculados al cliente
  const effectiveSocialActions = useMemo(() => {
    if (localHitos !== null) return localHitos;
    const rawActions = (overview?.actions || []) as DataRecord[];
    if (rawActions.length > 0) return rawActions;
    return [];
  }, [localHitos, overview]);

  const getHitosForMonth = (dateStr: string) => {
    return effectiveSocialActions.filter((a) => {
      const execDate = String(a.executed_at || a.date || "");
      if (!execDate) return false;
      const yrMo = execDate.slice(0, 7);
      return dateStr.startsWith(yrMo);
    });
  };

  // Sentimiento semanal real calculado sobre comments
  const weeklySentiment = useMemo(() => {
    const weeks: { label: string; pos: number; neu: number; neg: number; total: number }[] = [];
    const anchor = new Date();
    for (let i = 5; i >= 0; i--) {
      const start = new Date(anchor.getTime() - (i + 1) * 7 * 86400000);
      const end = new Date(anchor.getTime() - i * 7 * 86400000);
      const startStr = start.toISOString().slice(0, 10);
      const endStr = end.toISOString().slice(0, 10);
      const label = `Sem ${6 - i}`;
      let pos = 0;
      let neu = 0;
      let neg = 0;
      for (const c of comments) {
        const d = String(c.date || "").slice(0, 10);
        if (d >= startStr && d <= endStr) {
          const pol = String(c.polaridad || "").toLowerCase();
          if (pol === "positiva") pos++;
          else if (pol === "negativa") neg++;
          else neu++;
        }
      }
      weeks.push({ label, pos, neu, neg, total: pos + neu + neg });
    }
    const maxTotal = Math.max(1, ...weeks.map((w) => w.total));
    return { weeks, maxTotal };
  }, [comments]);

  const activeIdx = pinnedIdx !== null ? pinnedIdx : hoveredSocialIdx;

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (pinnedIdx !== null || isHoveringTooltip) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = e.clientX - rect.left;
    const svgX = (relX / rect.width) * 1200;
    let closestIdx = 0;
    let minDist = Infinity;
    monthlyGooglePoints.forEach((pt, i) => {
      const dist = Math.abs(pt.x - svgX);
      if (dist < minDist) {
        minDist = dist;
        closestIdx = i;
      }
    });
    setHoveredSocialIdx(closestIdx);
  };

  const handleSvgClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = e.clientX - rect.left;
    const svgX = (relX / rect.width) * 1200;
    let closestIdx = 0;
    let minDist = Infinity;
    monthlyGooglePoints.forEach((pt, i) => {
      const dist = Math.abs(pt.x - svgX);
      if (dist < minDist) {
        minDist = dist;
        closestIdx = i;
      }
    });
    setPinnedIdx((prev) => (prev === closestIdx ? null : closestIdx));
  };

  // Puntos válidos para trazar la línea
  const validPoints = monthlyGooglePoints.filter((pt) => pt.y !== null);
  const polylinePoints = validPoints.map((pt) => `${pt.x},${pt.y}`).join(" ");

  return (
    <section className="social-editorial-section" id="section-social-trend">
      <div className="social-section-tag">TENDENCIA</div>
      <h2 className="social-section-title">
        {showGoogleRatings ? "La nota en Google y el pulso semanal" : "El pulso semanal de la comunidad"}
      </h2>
      <p className="social-section-subtitle">
        {showGoogleRatings
          ? `Evolución mensual de las reseñas de Google Business Profile y correlación con hitos o eventos ejecutados.`
          : `Tendencia de volumen e interacción semanal de los comentarios en ${platformBadgeLabel}.`}
      </p>

      <div className="social-editorial-badges">
        <span className="social-badge is-active" style={{ background: "rgba(255, 255, 255, 0.05)" }}>
          {platformBadgeLabel}
        </span>
        <span className="social-role-badge">Reputación</span>
        <span className="social-role-badge">Calidad</span>
      </div>

      {showGoogleRatings ? (
        <>
          {/* Fila 1: Resumen de notas y barras de distribución */}
          <div className="social-trend-row">
            <div className="social-trend-box">
              <span className="social-trend-box-title">Nota promedio del período</span>
              <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginTop: "12px" }}>
                <span style={{ fontSize: "36px", fontWeight: 800, color: "var(--text, #f6f7fb)", lineHeight: 1 }}>
                  {overview?.kpis?.nota_media?.value !== undefined && overview?.kpis?.nota_media?.value !== null
                    ? Number(overview.kpis.nota_media.value).toFixed(1)
                    : "—"}
                </span>
                <span style={{ fontSize: "14px", color: "var(--muted, #8e96a8)", fontWeight: 500 }}>
                  de 5.0
                </span>
              </div>
              <div style={{ display: "flex", gap: "3px", marginTop: "10px", color: "var(--orange, #ff9f43)" }}>
                {[1, 2, 3, 4, 5].map((i) => {
                  const val = overview?.kpis?.nota_media?.value ?? 0;
                  return (
                    <Star
                      key={i}
                      size={18}
                      fill={i <= Math.round(Number(val)) ? "#ff9f43" : "none"}
                      stroke="#ff9f43"
                    />
                  );
                })}
              </div>
              <span style={{ fontSize: "11.5px", color: "var(--dim, #5e6677)", marginTop: "12px", display: "block" }}>
                Basado en {starCounts.total} {starCounts.total === 1 ? "reseña" : "reseñas"} en este período
              </span>
            </div>

            <div className="social-trend-box" style={{ flex: 1.5 }}>
              <span className="social-trend-box-title">Distribución de estrellas</span>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "12px" }}>
                {[5, 4, 3, 2, 1].map((stars) => {
                  const count = starCounts.counts[stars] || 0;
                  const pct = Math.round((count / starCounts.total) * 100);
                  const barColor =
                    stars >= 4
                      ? "var(--blue, #4285f4)"
                      : stars === 3
                      ? "var(--orange, #ff9f43)"
                      : "var(--red, #ff5c5c)";
                  return (
                    <div key={stars} style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "12px" }}>
                      <span style={{ width: "65px", color: "var(--muted, #8e96a8)" }}>
                        {stars} {stars === 1 ? "estrella" : "estrellas"}
                      </span>
                      <div style={{ flex: 1, height: "14px", background: "rgba(255, 255, 255, 0.06)", borderRadius: "4px", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: "4px" }} />
                      </div>
                      <span style={{ width: "32px", textAlign: "right", fontWeight: 700, color: "var(--text, #f6f7fb)" }}>
                        {pct}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Fila 2: Nota promedio mensual Google BP, últimos 12 meses */}
          <div className="social-trend-box" style={{ marginBottom: "14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "4px", flexWrap: "wrap", gap: "10px" }}>
              <div>
                <h4 style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: "var(--text, #f6f7fb)" }}>
                  Nota promedio mensual en Google BP (últimos 12 meses)
                </h4>
                <p style={{ margin: "4px 0 0 0", fontSize: "11.5px", color: "var(--muted, #8e96a8)" }}>
                  Promedio mensual de reseñas reales. Las banderas 🚩 marcan hitos. Haz clic en una columna para fijar el detalle.
                </p>
              </div>

              {canManageHitos && (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <button
                    type="button"
                    className="chart-hito-btn"
                    style={{ background: "rgba(255, 255, 255, 0.05)", border: "1px solid var(--line, rgba(255, 255, 255, 0.12))", color: "var(--text, #f6f7fb)" }}
                    onClick={() => setIsManageModalOpen(true)}
                    title="Administrar y listar todos los hitos registrados"
                  >
                    <List size={12} />
                    <span>Administrar hitos ({effectiveSocialActions.length})</span>
                  </button>
                  <button
                    type="button"
                    className="chart-hito-btn"
                    onClick={() => {
                      setEditingHito(null);
                      setIsHitoModalOpen(true);
                    }}
                    title="Registrar hito o acción ejecutada para correlacionar con las tendencias"
                  >
                    <Flag size={12} />
                    <span>+ Registrar hito</span>
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: "16px", margin: "14px 0", fontSize: "11.5px", flexWrap: "wrap" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "var(--text, #f6f7fb)", fontWeight: 600 }}>
                <span style={{ width: "9px", height: "9px", borderRadius: "50%", background: "#4285f4" }} />
                Calificación mensual (Google Business Profile)
              </span>
              {pinnedIdx !== null && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "#7d96ff", fontSize: "11px", fontWeight: 600 }}>
                  📌 Detalle fijado · Haz clic para desfijar
                </span>
              )}
            </div>

            <div
              ref={containerRef}
              style={{ width: "100%", overflow: "visible", position: "relative" }}
              onMouseLeave={() => {
                if (pinnedIdx === null && !isHoveringTooltip) {
                  setHoveredSocialIdx(null);
                }
              }}
            >
              <svg
                viewBox="0 0 1200 200"
                preserveAspectRatio="none"
                style={{ width: "100%", height: "210px", display: "block", cursor: "pointer" }}
                onMouseMove={handleMouseMove}
                onClick={handleSvgClick}
              >
                {/* Backdrop transparente para capturar clics */}
                <rect x="0" y="0" width="1200" height="200" fill="transparent" />

                <line x1="40" y1="35" x2="1170" y2="35" stroke="var(--line, rgba(255,255,255,0.06))" />
                <text x="20" y="39" fill="var(--dim, #5e6677)" fontSize="11">5.0</text>
                <line x1="40" y1="67" x2="1170" y2="67" stroke="var(--line, rgba(255,255,255,0.06))" />
                <text x="20" y="71" fill="var(--dim, #5e6677)" fontSize="11">4.0</text>
                <line x1="40" y1="100" x2="1170" y2="100" stroke="var(--line, rgba(255,255,255,0.06))" />
                <text x="20" y="104" fill="var(--dim, #5e6677)" fontSize="11">3.0</text>
                <line x1="40" y1="132" x2="1170" y2="132" stroke="var(--line, rgba(255,255,255,0.06))" />
                <text x="20" y="136" fill="var(--dim, #5e6677)" fontSize="11">2.0</text>

                {/* Línea real de calificación */}
                {polylinePoints && (
                  <polyline
                    fill="none"
                    stroke="#4285f4"
                    strokeWidth="2.5"
                    points={polylinePoints}
                  />
                )}

                {/* Banderas de hitos en los meses correspondientes */}
                {monthlyGooglePoints.map((pt, i) => {
                  const monthHitos = getHitosForMonth(pt.dateStr);
                  if (monthHitos.length === 0) return null;
                  return (
                    <g
                      key={`flag-${pt.label}`}
                      transform={`translate(${pt.x - 10}, 6)`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPinnedIdx(i);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <rect x="0" y="0" width="20" height="20" rx="4" fill="rgba(239, 68, 68, 0.25)" stroke="#ef4444" strokeWidth="1" />
                      <text x="10" y="14" textAnchor="middle" fontSize="11">🚩</text>
                    </g>
                  );
                })}

                {/* Puntos de cada mes con reseñas */}
                {monthlyGooglePoints.map((pt, i) => {
                  if (pt.y === null) return null;
                  const isAct = activeIdx === i;
                  return (
                    <g key={`pt-${pt.label}`}>
                      <circle
                        cx={pt.x}
                        cy={pt.y}
                        r={isAct ? 6.5 : 4.5}
                        fill="#4285f4"
                        stroke="#ffffff"
                        strokeWidth={isAct ? 2.5 : 1.5}
                      />
                    </g>
                  );
                })}

                {/* Columna activa y crosshair */}
                {activeIdx !== null && (
                  <g>
                    <line
                      x1={monthlyGooglePoints[activeIdx].x}
                      y1="25"
                      x2={monthlyGooglePoints[activeIdx].x}
                      y2="175"
                      stroke={pinnedIdx !== null ? "#7d96ff" : "rgba(255, 255, 255, 0.25)"}
                      strokeWidth={pinnedIdx !== null ? "2" : "1"}
                      strokeDasharray="3 3"
                    />
                  </g>
                )}

                {/* Ejes X (meses) */}
                {monthlyGooglePoints.map((pt) => (
                  <text
                    key={pt.label}
                    x={pt.x}
                    y="190"
                    fill={activeIdx !== null && monthlyGooglePoints[activeIdx].label === pt.label ? "var(--text, #f6f7fb)" : "var(--dim, #5e6677)"}
                    fontSize="11"
                    textAnchor="middle"
                    fontWeight={activeIdx !== null && monthlyGooglePoints[activeIdx].label === pt.label ? "bold" : "normal"}
                  >
                    {pt.label}
                  </text>
                ))}
              </svg>

              {/* Tooltip fijable estilo Semrush con X y Y anclados */}
              {activeIdx !== null && (
                (() => {
                  const pt = monthlyGooglePoints[activeIdx];
                  const monthHitos = getHitosForMonth(pt.dateStr);
                  const tooltipItems: ChartTooltipItem[] = [
                    {
                      label: "Calificación Google BP",
                      value: pt.val !== null ? `${pt.val.toFixed(1)} ⭐ (${pt.count} ${pt.count === 1 ? "reseña" : "reseñas"})` : "Sin reseñas en este mes",
                      color: "#4285f4",
                    },
                  ];
                  const hitosList: ChartHitoItem[] = monthHitos.map((h) => ({
                    id: String(h.id_item || h.id || ""),
                    title: String(h.titulo || "Hito"),
                    date: String(h.executed_at || h.date || "").slice(0, 10),
                    comment: String(h.comentario || ""),
                    category: String(h.dominio || h.categoria || "Social"),
                  }));
                  const cWidth = containerRef.current ? containerRef.current.clientWidth : 1200;
                  // Anclaje al punto X del gráfico para evitar que se mueva con el cursor
                  const anchorX = (pt.x / 1200) * cWidth;
                  const anchorY = pt.y !== null ? (pt.y / 200) * 210 : 80;

                  return (
                    <ChartTooltip
                      visible={true}
                      x={anchorX}
                      y={anchorY}
                      title={monthHitos.length > 0 ? `${monthHitos[0].executed_at ? String(monthHitos[0].executed_at).slice(0, 10) : pt.dateStr} (${pt.label})` : pt.label}
                      items={tooltipItems}
                      hitos={hitosList}
                      containerWidth={cWidth}
                      containerHeight={210}
                      canManageHitos={canManageHitos}
                      isPinned={pinnedIdx !== null}
                      onClose={() => setPinnedIdx(null)}
                      onMouseEnter={() => setIsHoveringTooltip(true)}
                      onMouseLeave={() => setIsHoveringTooltip(false)}
                      onEditHito={(h) => {
                        setEditingHito(h);
                        setIsHitoModalOpen(true);
                      }}
                      onDeleteHito={handleDeleteHito}
                    />
                  );
                })()
              )}
            </div>
          </div>

          <RegisterHitoModal
            isOpen={isHitoModalOpen}
            onClose={() => {
              setIsHitoModalOpen(false);
              setEditingHito(null);
            }}
            clientSlug={clientSlug}
            initialDomain="Social"
            initialHito={editingHito}
            onHitoCreated={() => {
              loadHitos();
            }}
          />

          <ManageHitosModal
            isOpen={isManageModalOpen}
            onClose={() => setIsManageModalOpen(false)}
            clientSlug={clientSlug}
            hitos={effectiveSocialActions}
            canManageHitos={canManageHitos}
            onEditHito={(h: ChartHitoItem) => {
              setEditingHito(h);
              setIsHitoModalOpen(true);
            }}
            onDeleteHito={handleDeleteHito}
            onOpenCreateModal={() => {
              setEditingHito(null);
              setIsHitoModalOpen(true);
            }}
          />

          {/* Fila 3: Sentimiento por semana (Datos reales calculados) */}
          <div className="social-trend-row">
            <div className="social-trend-box" style={{ width: "100%" }}>
              <h4 style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: 700, color: "var(--text, #f6f7fb)" }}>
                Sentimiento por semana
              </h4>
              <p style={{ margin: "0 0 16px 0", fontSize: "11.5px", color: "var(--muted, #8e96a8)" }}>
                Distribución semanal real en los últimos {windowDays} días
              </p>

              <div style={{ display: "flex", gap: "14px", marginBottom: "12px", fontSize: "11px" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "var(--muted, #8e96a8)" }}>
                  <span style={{ width: "8px", height: "8px", borderRadius: "2px", background: "#4285f4" }} />
                  Positivos
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "var(--muted, #8e96a8)" }}>
                  <span style={{ width: "8px", height: "8px", borderRadius: "2px", background: "#6b7280" }} />
                  Neutros
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "var(--muted, #8e96a8)" }}>
                  <span style={{ width: "8px", height: "8px", borderRadius: "2px", background: "#ef4444" }} />
                  Negativos
                </span>
              </div>

              {weeklySentiment.maxTotal === 1 && weeklySentiment.weeks.every((w) => w.total === 0) ? (
                <div style={{ padding: "20px 0", textAlign: "center", color: "var(--muted, #8e96a8)", fontSize: "12px" }}>
                  Sin comentarios registrados en las últimas semanas.
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "flex-end", height: "140px", gap: "12px", paddingTop: "10px" }}>
                  {weeklySentiment.weeks.map((w) => {
                    const hPos = (w.pos / weeklySentiment.maxTotal) * 120;
                    const hNeu = (w.neu / weeklySentiment.maxTotal) * 120;
                    const hNeg = (w.neg / weeklySentiment.maxTotal) * 120;
                    return (
                      <div key={w.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                        <div style={{ width: "100%", display: "flex", flexDirection: "column-reverse", borderRadius: "3px", overflow: "hidden" }}>
                          <div style={{ height: `${hPos}px`, background: "#4285f4" }} title={`${w.pos} positivos`} />
                          <div style={{ height: `${hNeu}px`, background: "#6b7280" }} title={`${w.neu} neutros`} />
                          <div style={{ height: `${hNeg}px`, background: "#ef4444" }} title={`${w.neg} negativos`} />
                        </div>
                        <span style={{ fontSize: "9.5px", color: "var(--dim, #5e6677)", whiteSpace: "nowrap" }}>{w.label}</span>
                        <span style={{ fontSize: "9px", color: "var(--muted, #8e96a8)" }}>({w.total})</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        /* Sentimiento por semana para Instagram / Facebook */
        <div className="social-trend-box" style={{ width: "100%" }}>
          <h4 style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: 700, color: "var(--text, #f6f7fb)" }}>
            Sentimiento por semana
          </h4>
          <p style={{ margin: "0 0 16px 0", fontSize: "11.5px", color: "var(--muted, #8e96a8)" }}>
            Comentarios nuevos por semana en {platformBadgeLabel}
          </p>

          <div style={{ display: "flex", gap: "14px", marginBottom: "12px", fontSize: "11px" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "var(--muted, #8e96a8)" }}>
              <span style={{ width: "8px", height: "8px", borderRadius: "2px", background: "#4285f4" }} />
              Positivos
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "var(--muted, #8e96a8)" }}>
              <span style={{ width: "8px", height: "8px", borderRadius: "2px", background: "#6b7280" }} />
              Neutros
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "var(--muted, #8e96a8)" }}>
              <span style={{ width: "8px", height: "8px", borderRadius: "2px", background: "#ef4444" }} />
              Negativos
            </span>
          </div>

          {weeklySentiment.maxTotal === 1 && weeklySentiment.weeks.every((w) => w.total === 0) ? (
            <div style={{ padding: "20px 0", textAlign: "center", color: "var(--muted, #8e96a8)", fontSize: "12px" }}>
              Sin comentarios registrados en las últimas semanas.
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "flex-end", height: "140px", gap: "12px", paddingTop: "10px" }}>
              {weeklySentiment.weeks.map((w) => {
                const hPos = (w.pos / weeklySentiment.maxTotal) * 120;
                const hNeu = (w.neu / weeklySentiment.maxTotal) * 120;
                const hNeg = (w.neg / weeklySentiment.maxTotal) * 120;
                return (
                  <div key={w.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                    <div style={{ width: "100%", display: "flex", flexDirection: "column-reverse", borderRadius: "3px", overflow: "hidden" }}>
                      <div style={{ height: `${hPos}px`, background: "#4285f4" }} title={`${w.pos} positivos`} />
                      <div style={{ height: `${hNeu}px`, background: "#6b7280" }} title={`${w.neu} neutros`} />
                      <div style={{ height: `${hNeg}px`, background: "#ef4444" }} title={`${w.neg} negativos`} />
                    </div>
                    <span style={{ fontSize: "9.5px", color: "var(--dim, #5e6677)", whiteSpace: "nowrap" }}>{w.label}</span>
                    <span style={{ fontSize: "9px", color: "var(--muted, #8e96a8)" }}>({w.total})</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
